import { AxiosError } from "axios";
import { Formik } from "formik";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import useToast from "@/contexts/toast-context";
import useLoading from "@/hooks/use-loading";
import { LojaIntegradaAccountConnectionMessages } from "@/messages/platforms/lojaIntegradaConnection/lojaIntegrada-account-connection.messages";
import lojaIntegradaService from "@/services/loja-integrada.service";

import {
  buildDefaultLojaIntegradaAccountConnectionFormData,
  buildLojaIntegradaConnectionFormSchema,
} from "./form-data";
import { LojaIntegradaAccountConnectionFormData } from "./types";

interface Props {
  messages: LojaIntegradaAccountConnectionMessages;
  onCompleteLojaIntegradaSubscription: () => void;
}

export function LojaIntegradaAccountConnection({
  messages,
  onCompleteLojaIntegradaSubscription,
}: Props) {
  const { startLoading, endLoading, isLoading } = useLoading();
  const { showToast } = useToast();

  const handleSubmit = async (values: LojaIntegradaAccountConnectionFormData) => {
    try {
      startLoading();
      await lojaIntegradaService.lojaIntegradaSubscription(values);
      onCompleteLojaIntegradaSubscription();
    } catch (error) {
      if (error instanceof AxiosError) {
        showToast({
          duration: 10000,
          text: JSON.stringify(error?.response?.data),
          type: "error",
        });
        return;
      }

      const text = messages.errorLojaIntegradaSubscription;
      showToast({ text, type: "error" });
    } finally {
      endLoading();
    }
  };

  return (
    <MDBox>
      <MDBox width="82%" textAlign="left" mx="auto" my={4}>
        <MDBox mb={1}>
          <MDTypography variant="body2" color="text">
            {messages.lojaIntegradaAccountConnectionText1}
          </MDTypography>
        </MDBox>
      </MDBox>

      <Formik
        initialValues={buildDefaultLojaIntegradaAccountConnectionFormData()}
        validationSchema={buildLojaIntegradaConnectionFormSchema(messages)}
        onSubmit={handleSubmit}
        validateOnBlur={false}
      >
        {(formik) => {
          const { values, handleChange, errors, submitForm } = formik;

          return (
            <MDBox width="82%" mx="auto" my={4}>
              <FormField
                variant="standard"
                label={messages.apiKeyLabel}
                name="ApiKey"
                value={values.ApiKey}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                error={!!errors.ApiKey}
                required
              />
              <MDButton variant="gradient" color="dark" onClick={submitForm} isLoading={isLoading}>
                {messages.continueButton}
              </MDButton>
            </MDBox>
          );
        }}
      </Formik>
    </MDBox>
  );
}
